import { checkPasswordAttempt } from "../model/PasswordLockSystem";
import { applyLockPass } from "../model/ProgressionSystem";
import type { ActionType } from "../types/action";
import type { ChapterConnection, ChapterData, ChapterNode, ChapterNodeType } from "../types/chapter";
import type { GameSave } from "../types/save";

const CHAPTER_ANCHOR_SPACING = 2;
const RAPID_END_UNDO_WINDOW_MS = 3000;

export type LevelSelectDirection = "left" | "right" | "up" | "down";
export type LevelSelectNodeStatus = "locked" | "unlocked" | "cleared" | "passed";

export interface LevelSelectRoute {
  readonly focus?: LevelSelectFocusRequest;
}

export type LevelSelectFocusRequest =
  | { readonly kind: "firstEnterable" }
  | { readonly kind: "node"; readonly nodeId: string }
  | { readonly kind: "afterLevelClear"; readonly levelId: string };

export interface LevelSelectNodeState {
  readonly id: string;
  readonly chapterId: string;
  readonly type: ChapterNodeType;
  readonly status: LevelSelectNodeStatus;
  readonly focusable: boolean;
  readonly enterable: boolean;
  readonly position: {
    readonly x: number;
    readonly y: number;
  };
  readonly collectibleGate: {
    readonly current: number;
    readonly required: number;
    readonly satisfied: boolean;
  } | null;
}

export interface LevelSelectEdgeState {
  readonly from: string;
  readonly to: string;
  readonly locked: boolean;
  readonly synthetic: boolean;
}

export interface LevelSelectGraphState {
  readonly nodes: readonly LevelSelectNodeState[];
  readonly edges: readonly LevelSelectEdgeState[];
}

export interface LevelSelectBrowseModeState {
  readonly kind: "browse";
  readonly rejectedNodeId: string | null;
}

export interface LevelSelectPasswordLockModeState {
  readonly kind: "passwordLock";
  readonly lockNodeId: string;
  readonly chapterId: string;
  readonly previousFocusNodeId: string;
  readonly attempt: readonly ActionType[];
  readonly attemptCount?: number;
  readonly failed: boolean;
  readonly collectibleGate: {
    readonly current: number;
    readonly required: number;
    readonly satisfied: boolean;
  };
  readonly passwordLength: number;
  readonly undoTimestamps?: readonly number[] | undefined;
}

export interface LevelSelectPasswordSuccessModeState {
  readonly kind: "passwordSuccess";
  readonly unlockedNodeId: string;
  readonly chapterId: string;
}

export type LevelSelectModeState =
  | LevelSelectBrowseModeState
  | LevelSelectPasswordLockModeState
  | LevelSelectPasswordSuccessModeState;

export interface LevelSelectState {
  readonly graph: LevelSelectGraphState;
  readonly focusedNodeId: string;
  readonly firstEnterableNodeId: string;
  readonly mode: LevelSelectModeState;
}

export type LevelSelectSignal =
  | { readonly kind: "none" }
  | { readonly kind: "focusChanged"; readonly nodeId: string }
  | { readonly kind: "blocked"; readonly nodeId: string | null }
  | { readonly kind: "enteredPasswordLock"; readonly nodeId: string }
  | { readonly kind: "cancelledPasswordLock"; readonly nodeId: string }
  | { readonly kind: "passwordAttemptRecorded"; readonly length: number }
  | { readonly kind: "passwordAttemptFailed"; readonly nodeId: string }
  | { readonly kind: "passwordUnlocked"; readonly nodeId: string }
  | { readonly kind: "launchLevel"; readonly levelId: string }
  | { readonly kind: "launchEnding" };

export interface LevelSelectTransitionResult {
  readonly state: LevelSelectState;
  readonly signal: LevelSelectSignal;
}

interface ChapterLayoutMeta {
  readonly chapter: ChapterData;
  readonly offsetX: number;
  readonly anchorX: number;
}

interface ChapterNodeEntry {
  readonly chapter: ChapterData;
  readonly node: ChapterNode;
  readonly state: LevelSelectNodeState;
}

export function createLevelSelectState(
  chapters: readonly ChapterData[],
  save: GameSave,
  route: LevelSelectRoute = {},
): LevelSelectState {
  const sortedChapters = [...chapters].sort((a, b) => a.order - b.order);
  const graph = createLevelSelectGraphState(sortedChapters, save);
  const firstEnterableNodeId = getFirstEnterableNodeId(graph.nodes);
  const focusedNodeId = resolveInitialFocusNodeId(graph.nodes, firstEnterableNodeId, route.focus);

  return {
    graph,
    focusedNodeId,
    firstEnterableNodeId,
    mode: { kind: "browse", rejectedNodeId: null },
  };
}

export function getNextEnterableNodeId(
  state: LevelSelectState,
  completedLevelId: string,
): string {
  const completedNode = state.graph.nodes.find((node) => node.id === completedLevelId);
  if (completedNode === undefined) {
    return state.firstEnterableNodeId;
  }

  const chapterNodes = state.graph.nodes.filter((node) => node.chapterId === completedNode.chapterId);
  const sortedChapterLevels = chapterNodes
    .filter((node) => node.type === "level" || node.type === "ending")
    .sort((a, b) => a.position.y - b.position.y);

  const index = sortedChapterLevels.findIndex((node) => node.id === completedLevelId);
  if (index >= 0) {
    for (let i = index + 1; i < sortedChapterLevels.length; i += 1) {
      const nextNode = sortedChapterLevels[i];
      if (nextNode !== undefined && nextNode.enterable) {
        return nextNode.id;
      }
    }
    return completedLevelId;
  }

  return state.firstEnterableNodeId;
}

export class LevelSelectController {
  private state: LevelSelectState;
  private saveState: GameSave;

  public constructor(
    private readonly chapters: readonly ChapterData[],
    save: GameSave,
    route: LevelSelectRoute = {},
  ) {
    this.saveState = save;
    this.state = createLevelSelectState(chapters, this.saveState, route);
  }

  public open(route: LevelSelectRoute = {}): LevelSelectState {
    this.state = createLevelSelectState(this.chapters, this.saveState, route);
    return this.state;
  }

  public getState(): LevelSelectState {
    return this.state;
  }

  public getSave(): GameSave {
    return this.saveState;
  }

  public move(direction: LevelSelectDirection): LevelSelectTransitionResult {
    if (this.state.mode.kind !== "browse") {
      return this.commitState(this.state, { kind: "none" });
    }

    const current = getNodeById(this.state.graph.nodes, this.state.focusedNodeId);

    const bridgeEdge = findBridgeEdge(this.state.graph.edges, this.state.graph.nodes, current, direction);
    if (bridgeEdge !== null) {
      if (bridgeEdge.locked) {
        const targetChapterId = bridgeEdge.from === current.id
          ? getNodeById(this.state.graph.nodes, bridgeEdge.to).chapterId
          : getNodeById(this.state.graph.nodes, bridgeEdge.from).chapterId;
        const chapter = getChapterById(this.chapters, targetChapterId);
        const lockNode = chapter.nodes.find((n) => n.type === "lock");
        const lockNodeId = lockNode?.id ?? chapter.id;
        const collectibleGate = getRequiredCollectibleGate(chapter, this.chapters, this.saveState);
        const previousChapterCleared = isPreviousChapterFullyCleared(chapter, this.chapters, this.saveState);

        if (!collectibleGate.satisfied || !previousChapterCleared) {
          return this.commitState(
            {
              ...this.state,
              focusedNodeId: current.id,
              mode: { kind: "browse", rejectedNodeId: lockNodeId },
            },
            { kind: "blocked", nodeId: lockNodeId },
          );
        }

        const nextState: LevelSelectState = {
          ...this.state,
          focusedNodeId: current.id,
          mode: {
            kind: "passwordLock",
            lockNodeId: lockNodeId,
            chapterId: chapter.id,
            previousFocusNodeId: current.id,
            attempt: [],
            attemptCount: 0,
            failed: false,
            collectibleGate,
            passwordLength: chapter.unlock?.password.length ?? 0,
            undoTimestamps: undefined,
          },
        };

        return this.commitState(nextState, {
          kind: "enteredPasswordLock",
          nodeId: lockNodeId,
        });
      }

      const targetId = bridgeEdge.from === current.id ? bridgeEdge.to : bridgeEdge.from;
      const target = getNodeById(this.state.graph.nodes, targetId);
      return this.commitState(
        {
          ...this.state,
          focusedNodeId: target.id,
          mode: { kind: "browse", rejectedNodeId: null },
        },
        { kind: "focusChanged", nodeId: target.id },
      );
    }

    const candidate = findDirectionalCandidate(this.state.graph.nodes, current, direction);
    if (candidate === null) {
      return this.commitState(
        {
          ...this.state,
          mode: { kind: "browse", rejectedNodeId: null },
        },
        { kind: "blocked", nodeId: null },
      );
    }

    if (!candidate.enterable) {
      return this.commitState(
        {
          ...this.state,
          mode: { kind: "browse", rejectedNodeId: candidate.id },
        },
        { kind: "blocked", nodeId: candidate.id },
      );
    }

    return this.commitState(
      {
        ...this.state,
        focusedNodeId: candidate.id,
        mode: { kind: "browse", rejectedNodeId: null },
      },
      { kind: "focusChanged", nodeId: candidate.id },
    );
  }

  public activateFocused(): LevelSelectTransitionResult {
    if (this.state.mode.kind === "passwordSuccess") {
      return this.commitState(this.state, { kind: "none" });
    }

    if (this.state.mode.kind === "passwordLock") {
      return this.submitPasswordLock();
    }

    const focused = getNodeById(this.state.graph.nodes, this.state.focusedNodeId);

    if (!focused.enterable) {
      return this.commitState(
        {
          ...this.state,
          mode: { kind: "browse", rejectedNodeId: focused.id },
        },
        { kind: "blocked", nodeId: focused.id },
      );
    }

    if (focused.type === "level") {
      return this.commitState(this.state, { kind: "launchLevel", levelId: focused.id });
    }

    if (focused.type === "ending") {
      return this.commitState(this.state, { kind: "launchEnding" });
    }

    const chapter = getChapterById(this.chapters, focused.chapterId);
    const collectibleGate = getRequiredCollectibleGate(chapter, this.chapters, this.saveState);
    const previousChapterCleared = isPreviousChapterFullyCleared(chapter, this.chapters, this.saveState);
    if (!collectibleGate.satisfied || !previousChapterCleared) {
      return this.commitState(
        {
          ...this.state,
          mode: { kind: "browse", rejectedNodeId: focused.id },
        },
        { kind: "blocked", nodeId: focused.id },
      );
    }

    return this.commitState(
      {
        ...this.state,
        mode: {
          kind: "passwordLock",
          lockNodeId: focused.id,
          chapterId: focused.chapterId,
          previousFocusNodeId: focused.id,
          attempt: [],
          attemptCount: 0,
          failed: false,
          collectibleGate,
          passwordLength: chapter.unlock?.password.length ?? 0,
          undoTimestamps: undefined,
        },
      },
      { kind: "enteredPasswordLock", nodeId: focused.id },
    );
  }

  public cancelPasswordLock(): LevelSelectTransitionResult {
    if (this.state.mode.kind !== "passwordLock") {
      return this.commitState(this.state, { kind: "none" });
    }

    const previousFocusNodeId = this.state.mode.previousFocusNodeId;
    return this.commitState(
      {
        ...this.state,
        focusedNodeId: previousFocusNodeId,
        mode: { kind: "browse", rejectedNodeId: null },
      },
      { kind: "cancelledPasswordLock", nodeId: previousFocusNodeId },
    );
  }

  public recordPasswordAction(action: ActionType): LevelSelectTransitionResult {
    if (this.state.mode.kind !== "passwordLock") {
      return this.commitState(this.state, { kind: "none" });
    }

    const chapter = getChapterById(this.chapters, this.state.mode.chapterId);
    const expected = chapter.unlock?.password ?? [];
    const passwordLength = expected.length;
    const attempt = appendPasswordAttempt(this.state.mode.attempt, action, expected.length);
    const attemptCount = (this.state.mode.attemptCount ?? this.state.mode.attempt.length) + 1;

    let undoTimestamps: readonly number[] | undefined = this.state.mode.undoTimestamps;
    if (chapter.kind === "ending" && action === "undo") {
      const now = Date.now();
      const recent = [...(undoTimestamps ?? []), now].filter((t) => now - t <= RAPID_END_UNDO_WINDOW_MS);
      undoTimestamps = recent;

      if (recent.length >= 7) {
        return this.commitState(
          {
            ...this.state,
            mode: {
              ...this.state.mode,
              attempt,
              attemptCount,
              failed: false,
              undoTimestamps: recent,
            },
          },
          { kind: "launchEnding" },
        );
      }
    }

    const nextMode: LevelSelectPasswordLockModeState = {
      ...this.state.mode,
      attempt,
      attemptCount,
      failed: false,
      undoTimestamps,
    };

    const shouldCheck = passwordLength > 0 && attemptCount % passwordLength === 0;
    if (shouldCheck) {
      const check = checkPasswordAttempt(expected, attempt);
      if (check.accepted) {
        return this.unlockPasswordLock(chapter);
      }

      return this.commitState(
        {
          ...this.state,
          mode: {
            ...this.state.mode,
            attempt: [],
            attemptCount: 0,
            failed: attemptCount > 0,
            undoTimestamps: undefined,
          },
        },
        {
          kind: "passwordAttemptFailed",
          nodeId: this.state.mode.lockNodeId,
        },
      );
    }

    const nextState: LevelSelectState = {
      ...this.state,
      mode: nextMode,
    };

    return this.commitState(nextState, { kind: "passwordAttemptRecorded", length: attempt.length });
  }

  public submitPasswordLock(): LevelSelectTransitionResult {
    if (this.state.mode.kind !== "passwordLock") {
      return this.commitState(this.state, { kind: "none" });
    }

    const chapter = getChapterById(this.chapters, this.state.mode.chapterId);
    const expected = chapter.unlock?.password ?? [];
    const check = checkPasswordAttempt(expected, this.state.mode.attempt);
    const attemptCount = this.state.mode.attemptCount ?? this.state.mode.attempt.length;

    if (check.accepted) {
      return this.unlockPasswordLock(chapter);
    }

    const nextState: LevelSelectState = {
      ...this.state,
      mode: {
        ...this.state.mode,
        attempt: [],
        attemptCount: 0,
        failed: attemptCount > 0,
        undoTimestamps: undefined,
      },
    };

    if (attemptCount === 0) {
      return this.commitState(nextState, { kind: "none" });
    }

    return this.commitState(nextState, {
      kind: "passwordAttemptFailed",
      nodeId: this.state.mode.lockNodeId,
    });
  }

  public settlePasswordUnlockSuccess(): LevelSelectTransitionResult {
    if (this.state.mode.kind !== "passwordSuccess") {
      return this.commitState(this.state, { kind: "none" });
    }

    return this.commitState(
      {
        ...this.state,
        mode: { kind: "browse", rejectedNodeId: null },
      },
      { kind: "none" },
    );
  }

  public settlePasswordFailure(): LevelSelectTransitionResult {
    if (this.state.mode.kind !== "passwordLock" || !this.state.mode.failed) {
      return this.commitState(this.state, { kind: "none" });
    }

    return this.commitState(
      {
        ...this.state,
        mode: {
          ...this.state.mode,
          failed: false,
          undoTimestamps: undefined,
        },
      },
      { kind: "none" },
    );
  }

  private unlockPasswordLock(chapter: ChapterData): LevelSelectTransitionResult {
    const update = applyLockPass(this.saveState, chapter);
    this.saveState = update.save;

    const unlockedNodeId = update.newlyUnlockedLevelIds[0] ?? chapter.levels[0] ?? this.state.focusedNodeId;
    const focus = unlockedNodeId === undefined
      ? undefined
      : { kind: "node" as const, nodeId: unlockedNodeId };
    const browseState = createLevelSelectState(this.chapters, this.saveState, focus === undefined ? {} : { focus });
    const nextState: LevelSelectState = {
      ...browseState,
      mode: {
        kind: "passwordSuccess",
        unlockedNodeId,
        chapterId: chapter.id,
      },
    };

    return this.commitState(nextState, {
      kind: "passwordUnlocked",
      nodeId: unlockedNodeId,
    });
  }

  private commitState(state: LevelSelectState, signal: LevelSelectSignal): LevelSelectTransitionResult {
    this.state = state;
    return {
      state,
      signal,
    };
  }
}

function appendPasswordAttempt(
  attempt: readonly ActionType[],
  action: ActionType,
  passwordLength: number,
): readonly ActionType[] {
  const nextAttempt = [...attempt, action];
  if (passwordLength <= 0 || nextAttempt.length <= passwordLength) {
    return nextAttempt;
  }

  return nextAttempt.slice(nextAttempt.length - passwordLength);
}

function createLevelSelectGraphState(
  chapters: readonly ChapterData[],
  save: GameSave,
): LevelSelectGraphState {
  const layouts = createChapterLayouts(chapters);
  const nodes = layouts.flatMap((layout) =>
    layout.chapter.nodes.map((node) => createChapterNodeEntry(layout, node, chapters, save).state)
  );

  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  const edges: LevelSelectEdgeState[] = [];

  for (const layout of layouts) {
    for (const connection of layout.chapter.connections) {
      edges.push(createEdgeState(connection, nodeById, false));
    }
  }

  for (let index = 0; index < layouts.length - 1; index += 1) {
    const current = layouts[index]!.chapter;
    const next = layouts[index + 1]!.chapter;
    const bridge = createChapterBridgeConnection(current, next);
    if (bridge !== null) {
      edges.push(createEdgeState(bridge, nodeById, true, chapters, save));
    }
  }

  return { nodes, edges };
}

function createChapterLayouts(chapters: readonly ChapterData[]): readonly ChapterLayoutMeta[] {
  let cursorX = 0;

  return chapters.map((chapter) => {
    const firstPlayableNode = chapter.nodes.find(
      (node) => node.id === chapter.levels[0] && (node.type === "level" || node.type === "ending"),
    );
    const anchorX = firstPlayableNode?.x ?? 0;
    const layout: ChapterLayoutMeta = {
      chapter,
      offsetX: cursorX,
      anchorX,
    };
    cursorX += CHAPTER_ANCHOR_SPACING;
    return layout;
  });
}

function createChapterNodeEntry(
  layout: ChapterLayoutMeta,
  node: ChapterNode,
  chapters: readonly ChapterData[],
  save: GameSave,
): ChapterNodeEntry {
  const chapter = layout.chapter;
  const collectibleGate = node.type === "lock" ? getCollectibleGate(chapter, chapters, save) : null;
  const gateSatisfied = collectibleGate?.satisfied ?? true;
  const passedLock = node.type === "lock" && isPassedLock(node, chapter, save);
  const unlockedLevel = node.type !== "lock" && save.progression.unlockedLevels.includes(node.id);
  const clearedLevel = node.type !== "lock" && save.progression.clearedLevels.includes(node.id);
  const status = resolveNodeStatus(node.type, { passedLock, unlockedLevel, clearedLevel });
  const enterable = resolveNodeEnterable(node.type, { passedLock, unlockedLevel, gateSatisfied });

  return {
    chapter,
    node,
    state: {
      id: node.id,
      chapterId: chapter.id,
      type: node.type,
      status,
      focusable: enterable,
      enterable,
      position: {
        x: layout.offsetX + (node.x - layout.anchorX),
        y: node.y,
      },
      collectibleGate,
    },
  };
}

function resolveNodeStatus(
  nodeType: ChapterNodeType,
  status: {
    readonly passedLock: boolean;
    readonly unlockedLevel: boolean;
    readonly clearedLevel: boolean;
  },
): LevelSelectNodeStatus {
  if (nodeType === "lock") {
    return status.passedLock ? "passed" : "locked";
  }

  if (status.clearedLevel) {
    return "cleared";
  }

  if (status.unlockedLevel) {
    return "unlocked";
  }

  return "locked";
}

function resolveNodeEnterable(
  nodeType: ChapterNodeType,
  status: {
    readonly passedLock: boolean;
    readonly unlockedLevel: boolean;
    readonly gateSatisfied: boolean;
  },
): boolean {
  if (nodeType === "lock") {
    return false;
  }

  return status.unlockedLevel;
}

function createEdgeState(
  connection: ChapterConnection,
  nodeById: ReadonlyMap<string, LevelSelectNodeState>,
  synthetic: boolean,
  chapters?: readonly ChapterData[],
  save?: GameSave,
): LevelSelectEdgeState {
  const from = nodeById.get(connection.from);
  const to = nodeById.get(connection.to);

  if (from === undefined || to === undefined) {
    throw new Error(`Cannot create edge ${connection.from} -> ${connection.to}: node missing.`);
  }

  let locked: boolean;
  if (synthetic && chapters !== undefined && save !== undefined) {
    const targetChapterId = to.chapterId;
    const targetChapter = chapters.find((c) => c.id === targetChapterId);
    if (targetChapter !== undefined) {
      const lockNode = targetChapter.nodes.find((n) => n.type === "lock");
      const lockId = lockNode?.id ?? targetChapter.id;
      const isPassed = save.progression.passedLocks.includes(lockId)
        || save.progression.unlockedChapters.includes(targetChapter.id);
      locked = !isPassed;
    } else {
      locked = true;
    }
  } else {
    locked = !(from.enterable && to.enterable);
  }

  return {
    from: connection.from,
    to: connection.to,
    locked,
    synthetic,
  };
}

function createChapterBridgeConnection(
  current: ChapterData,
  next: ChapterData,
): ChapterConnection | null {
  const from = current.levels[0];
  const to = next.levels[0];

  if (from === undefined || to === undefined) {
    return null;
  }

  return { from, to };
}

function resolveInitialFocusNodeId(
  nodes: readonly LevelSelectNodeState[],
  firstEnterableNodeId: string,
  focus: LevelSelectFocusRequest | undefined,
): string {
  if (focus?.kind === "node") {
    const direct = nodes.find((node) => node.id === focus.nodeId && node.enterable);
    if (direct !== undefined) {
      return direct.id;
    }
  }

  if (focus?.kind === "afterLevelClear") {
    const clearedNode = nodes.find((node) => node.id === focus.levelId);
    if (clearedNode !== undefined) {
      const chapterNodes = nodes.filter((node) => node.chapterId === clearedNode.chapterId);
      const sortedChapterLevels = chapterNodes
        .filter((node) => node.type === "level" || node.type === "ending")
        .sort((a, b) => a.position.y - b.position.y);

      const clearedIndex = sortedChapterLevels.findIndex((node) => node.id === focus.levelId);
      if (clearedIndex >= 0) {
        for (let i = clearedIndex + 1; i < sortedChapterLevels.length; i += 1) {
          const nextNode = sortedChapterLevels[i];
          if (nextNode !== undefined && nextNode.enterable) {
            return nextNode.id;
          }
        }
        return focus.levelId;
      }
    }
  }

  return firstEnterableNodeId;
}

function getFirstEnterableNodeId(nodes: readonly LevelSelectNodeState[]): string {
  const first = getEnterableNodes(nodes)[0];
  if (first === undefined) {
    throw new Error("LevelSelect requires at least one enterable node.");
  }

  return first.id;
}

function getEnterableNodes(nodes: readonly LevelSelectNodeState[]): readonly LevelSelectNodeState[] {
  return [...nodes]
    .filter((node) => node.enterable)
    .sort((a, b) => (a.position.x - b.position.x) || (a.position.y - b.position.y));
}

function getCollectibleGate(
  chapter: ChapterData,
  chapters: readonly ChapterData[],
  save: GameSave,
): LevelSelectNodeState["collectibleGate"] {
  if (chapter.unlock === undefined) {
    return null;
  }

  const chapterIndex = chapters.findIndex((c) => c.id === chapter.id);

  let current = 0;
  if (chapterIndex > 0) {
    const previousChapter = chapters[chapterIndex - 1]!;
    const completedGroups = save.collectibles.completedGroupsByLevel ?? {};
    const prevChapterNumber = String(previousChapter.order);
    for (const levelId of previousChapter.levels) {
      const groups = completedGroups[levelId] ?? [];
      if (groups.includes(prevChapterNumber)) {
        current += 1;
      }
    }
  }

  const required = chapter.unlock.requiredPreviousChapterCompletedLevels ?? 0;

  return {
    current,
    required,
    satisfied: current >= required,
  };
}

function getRequiredCollectibleGate(
  chapter: ChapterData,
  chapters: readonly ChapterData[],
  save: GameSave,
): NonNullable<LevelSelectNodeState["collectibleGate"]> {
  const gate = getCollectibleGate(chapter, chapters, save);
  if (gate === null) {
    throw new Error(`Chapter ${chapter.id} does not define a collectible gate.`);
  }

  return gate;
}

function isPreviousChapterFullyCleared(
  chapter: ChapterData,
  chapters: readonly ChapterData[],
  save: GameSave,
): boolean {
  const chapterIndex = chapters.findIndex((c) => c.id === chapter.id);
  if (chapterIndex <= 0) {
    return true;
  }

  const previousChapter = chapters[chapterIndex - 1]!;
  return previousChapter.levels.every((levelId) => save.progression.clearedLevels.includes(levelId));
}

function isPassedLock(node: ChapterNode, chapter: ChapterData, save: GameSave): boolean {
  return save.progression.passedLocks.includes(node.id)
    || save.progression.unlockedChapters.includes(chapter.id);
}

function getNodeById(nodes: readonly LevelSelectNodeState[], nodeId: string): LevelSelectNodeState {
  const node = nodes.find((entry) => entry.id === nodeId);
  if (node === undefined) {
    throw new Error(`LevelSelect node ${nodeId} not found.`);
  }

  return node;
}

function getChapterById(chapters: readonly ChapterData[], chapterId: string): ChapterData {
  const chapter = chapters.find((entry) => entry.id === chapterId);
  if (chapter === undefined) {
    throw new Error(`LevelSelect chapter ${chapterId} not found.`);
  }

  return chapter;
}

function findDirectionalCandidate(
  nodes: readonly LevelSelectNodeState[],
  current: LevelSelectNodeState,
  direction: LevelSelectDirection,
): LevelSelectNodeState | null {
  const isHorizontal = direction === "left" || direction === "right";

  const candidates = nodes
    .filter((node) => {
      if (node.id === current.id) return false;
      if (!node.focusable) return false;
      if (isHorizontal && node.chapterId !== current.chapterId) return false;

      const deltaX = node.position.x - current.position.x;
      const deltaY = node.position.y - current.position.y;
      return isCandidateInDirection(direction, deltaX, deltaY);
    })
    .map((node) => ({
      node,
      deltaX: node.position.x - current.position.x,
      deltaY: node.position.y - current.position.y,
    }))
    .filter(({ deltaX, deltaY }) => isCandidateInDirection(direction, deltaX, deltaY))
    .sort((a, b) => compareDirectionalCandidate(direction, a.deltaX, a.deltaY, b.deltaX, b.deltaY));

  return candidates[0]?.node ?? null;
}

function findBridgeEdge(
  edges: readonly LevelSelectEdgeState[],
  nodes: readonly LevelSelectNodeState[],
  current: LevelSelectNodeState,
  direction: LevelSelectDirection,
): LevelSelectEdgeState | null {
  const isHorizontal = direction === "left" || direction === "right";
  if (!isHorizontal) {
    return null;
  }

  const outgoing = edges.find((e) => e.synthetic && e.from === current.id);
  if (outgoing !== undefined) {
    const target = getNodeById(nodes, outgoing.to);
    const deltaX = target.position.x - current.position.x;
    const deltaY = target.position.y - current.position.y;
    if (isCandidateInDirection(direction, deltaX, deltaY)) {
      return outgoing;
    }
  }

  const incoming = edges.find((e) => e.synthetic && e.to === current.id);
  if (incoming !== undefined) {
    const source = getNodeById(nodes, incoming.from);
    const deltaX = source.position.x - current.position.x;
    const deltaY = source.position.y - current.position.y;
    if (isCandidateInDirection(direction, deltaX, deltaY)) {
      return incoming;
    }
  }

  return null;
}

function isCandidateInDirection(
  direction: LevelSelectDirection,
  deltaX: number,
  deltaY: number,
): boolean {
  switch (direction) {
    case "left":
      return deltaX < 0;
    case "right":
      return deltaX > 0;
    case "up":
      return deltaY < 0;
    case "down":
      return deltaY > 0;
    default:
      return false;
  }
}

function compareDirectionalCandidate(
  direction: LevelSelectDirection,
  leftDeltaX: number,
  leftDeltaY: number,
  rightDeltaX: number,
  rightDeltaY: number,
): number {
  const leftPrimary = getPrimaryDistance(direction, leftDeltaX, leftDeltaY);
  const rightPrimary = getPrimaryDistance(direction, rightDeltaX, rightDeltaY);
  if (leftPrimary !== rightPrimary) {
    return leftPrimary - rightPrimary;
  }

  const leftSecondary = getSecondaryDistance(direction, leftDeltaX, leftDeltaY);
  const rightSecondary = getSecondaryDistance(direction, rightDeltaX, rightDeltaY);
  if (leftSecondary !== rightSecondary) {
    return leftSecondary - rightSecondary;
  }

  return (Math.abs(leftDeltaX) + Math.abs(leftDeltaY)) - (Math.abs(rightDeltaX) + Math.abs(rightDeltaY));
}

function getPrimaryDistance(
  direction: LevelSelectDirection,
  deltaX: number,
  deltaY: number,
): number {
  return direction === "left" || direction === "right"
    ? Math.abs(deltaX)
    : Math.abs(deltaY);
}

function getSecondaryDistance(
  direction: LevelSelectDirection,
  deltaX: number,
  deltaY: number,
): number {
  return direction === "left" || direction === "right"
    ? Math.abs(deltaY)
    : Math.abs(deltaX);
}
